import { APP_INITIALIZER, Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationRegions } from '@trustedshops/tswp-carrier-b2b-contracts';
import {
  BootstrapperBase,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  NavigationEntries,
  Routes,
} from '@trustedshops/tswp-plugin-settings-contracts';
import { TOKENS, PluginRouter } from '@trustedshops/tswp-core-plugins';
import {
  NavigationItem,
  NavigationService,
  TOKENS as TOKENS_UI,
  UserInteractionType,
} from '@trustedshops/tswp-core-ui';

@Injectable()
export class SettingsBootstrapper implements BootstrapperBase {
  //#region Public Constants
  public static readonly TYPE: string =
    '@trustedshops/tswp-carrier-b2b:SettingsBootstrapper';
  //#endregion

  /**
   * Resolved unconditionally at the end of `initialize()` — no early return,
   * no external I/O gates it, so it can never hang. Created here in the
   * constructor (not in `initialize()`) so a dependent bootstrapper can hold
   * a reference to it before Angular ever invokes `initialize()`. This is
   * the explicit, awaitable replacement for the load-order guarantee the
   * old standalone plugin got for free from its manifest's
   * `dependencies: ['trustedshops_tswp-plugin-settings']` entry — native
   * carrier `APP_INITIALIZER`s have no such manifest, and run concurrently
   * via `Promise.all`, not sequentially by provider array position.
   */
  public readonly initialized: Promise<void>;

  private _resolveInitialized!: () => void;

  //#region Ctor
  public constructor(
    @Inject(TOKENS.PluginRouter)
    private readonly _pluginRouter: PluginRouter,

    @Inject(TOKENS_UI.NavigationService)
    private readonly _navigationService: NavigationService,

    @Inject(COMMON_TOKENS.LogService)
    private readonly _logService: LogService,

    private readonly _translateService: TranslateService
  ) {
    this.initialized = new Promise<void>((resolve) => {
      this._resolveInitialized = resolve;
    });
  }
  //#endregion

  //#region Public Methods
  public async initialize(): Promise<void> {
    this._logService.debug(
      SettingsBootstrapper.TYPE,
      'Initializing settings route and navigation item'
    );

    this.registerRoute();
    this.registerNavigationItem();

    this._resolveInitialized();
  }
  //#endregion

  //#region Private Methods
  private registerRoute(): void {
    // No parent id: root-level 'settings' route, matching the old plugin's
    // own registerRoutes() call. No `plugin` field: that triggers
    // PluginLoadedRouteGuard, meant for the legacy dynamically-loaded
    // plugin system — irrelevant to native carrier code.
    this._pluginRouter.addRoutes([
      {
        id: Routes.Settings,
        webComponent: 'tswp-route-content',
        path: 'settings',
      },
    ]);
  }

  private registerNavigationItem(): void {
    const navigationItem = {
      id: NavigationEntries.Settings,
      label: 'carrier.settings.navigation-main',
      order: 1000,
      icon: 'helios-icon helios-icon-feature-navigation-settings',
      userInteraction: this._navigationService
        .getUserInteractionFactory(UserInteractionType.NavigateToFirstChild)
        .create({
          parentNavigationItemId: NavigationEntries.Settings,
          childrenRegion: NavigationRegions.SecondaryMenu,
        }),
    } as unknown as NavigationItem;

    this._translateService
      .get(navigationItem.label)
      .subscribe((translation) => {
        navigationItem.label = translation;
        this._navigationService.registerNavigationItems(
          [navigationItem],
          NavigationRegions.PrimaryMenu
        );
      });
  }
  //#endregion
}

export const SettingsInitializer = [
  SettingsBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (initializer: SettingsBootstrapper) => {
      return () => initializer.initialize();
    },
    deps: [SettingsBootstrapper],
  },
];
